import { useState } from 'react'
import { Button, Input, Label } from '../ui'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../ui'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui'
import { toast } from 'sonner'
import { BPSCalculation } from '../info/BPSCalculation'
import {
  DepositRiskThresholdSelect,
  type DepositRiskThresholdValue,
} from '../DepositRiskThresholdSelect'

export interface LiquidityProvider {
  id: string
  name: string
  bps: number
  valiantBps?: number | null
}

interface CreateGroupModalProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  liquidityProviders: LiquidityProvider[]
  onCreateGroup: (variables: {
    input: {
      name: string
      bps: number
      liquidityProviderId: number
      defaultDepositRiskThreshold: DepositRiskThresholdValue
    }
  }) => Promise<{ error?: { message: string } }>
}

export function CreateGroupModal({
  open,
  onOpenChange,
  liquidityProviders,
  onCreateGroup,
}: CreateGroupModalProps) {
  const [name, setName] = useState('')
  const [bps, setBps] = useState('50')
  const [selectedLpId, setSelectedLpId] = useState<string>('')
  const [depositRiskThreshold, setDepositRiskThreshold] =
    useState<DepositRiskThresholdValue>('MEDIUM')
  const [isSubmitting, setIsSubmitting] = useState(false)

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()

    if (!selectedLpId || !name || !bps) {
      return
    }

    setIsSubmitting(true)
    try {
      const result = await onCreateGroup({
        input: {
          name,
          bps: parseInt(bps),
          liquidityProviderId: parseInt(selectedLpId),
          defaultDepositRiskThreshold: depositRiskThreshold,
        },
      })

      if (result.error) {
        toast.error('Failed to create group', {
          description: result.error.message,
        })
        return
      }

      // Success - show toast, close modal and reset form
      toast.success('Group created successfully!')
      onOpenChange(false)
      setName('')
      setBps('50')
      setSelectedLpId('')
      setDepositRiskThreshold('MEDIUM')
    } catch (error) {
      toast.error('Failed to create group', {
        description: error instanceof Error ? error.message : 'An unexpected error occurred',
      })
    } finally {
      setIsSubmitting(false)
    }
  }

  const selectedLp = liquidityProviders.find((lp) => lp.id === selectedLpId)

  // Check if all required fields are filled
  const isFormValid = name.trim() !== '' && bps !== '' && selectedLpId !== '' && parseInt(bps) >= 50

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[425px]">
        <DialogHeader>
          <DialogTitle>Create Group</DialogTitle>
          <DialogDescription>
            Create a new group with a liquidity provider and basis points.
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={handleSubmit}>
          <div className="grid gap-4 py-4">
            <div className="grid gap-2">
              <Label htmlFor="name">Name</Label>
              <Input
                id="name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Enter group name"
                required
              />
            </div>
            <div className="grid gap-2">
              <Label htmlFor="bps">BPS Taken</Label>
              <Input
                id="bps"
                type="number"
                min="50"
                step="1"
                max="10000"
                value={bps}
                onChange={(e) => {
                  const value = e.target.value

                  // Prevent minus sign input
                  if (value.includes('-')) {
                    return
                  }

                  // Allow empty value for typing
                  if (value === '') {
                    setBps(value)
                    return
                  }

                  const numValue = parseInt(value)

                  // Only allow integers >= 0
                  if (Number.isInteger(numValue) && numValue >= 0) {
                    setBps(value)
                  }
                }}
                onBlur={(e) => {
                  // Auto-correct to 50 if empty or less than 50 on blur
                  const value = e.target.value
                  if (value === '' || parseInt(value) < 50) {
                    setBps('50')
                  }
                }}
                placeholder="50"
                required
              />
            </div>
            <div className="grid gap-2">
              <Label htmlFor="liquidity-provider">Liquidity Provider</Label>
              <Select value={selectedLpId} onValueChange={setSelectedLpId} required>
                <SelectTrigger id="liquidity-provider">
                  <SelectValue placeholder="Select a liquidity provider" />
                </SelectTrigger>
                <SelectContent>
                  {liquidityProviders.map((lp) => (
                    <SelectItem key={lp.id} value={lp.id}>
                      {lp.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {selectedLp && (
                <div className="text-sm text-muted-foreground">
                  {selectedLp.valiantBps != null ? (
                    <>
                      LP + Valiant: <span className="font-bold">{selectedLp.bps} BPS</span>
                    </>
                  ) : (
                    <>
                      LP Fee: <span className="font-bold">{selectedLp.bps} BPS</span>
                    </>
                  )}
                </div>
              )}
              {selectedLp && (
                <BPSCalculation
                  bps={parseInt(bps) || 0}
                  lpFee={selectedLp.bps}
                  valiantBps={selectedLp.valiantBps}
                />
              )}
            </div>
            <DepositRiskThresholdSelect
              value={depositRiskThreshold}
              onValueChange={setDepositRiskThreshold}
              disabled={isSubmitting}
              helperText="New companies in this group inherit this threshold. Deposits above it are flagged for compliance review."
            />
          </div>
          <DialogFooter>
            <Button
              variant="main_action"
              type="submit"
              disabled={isSubmitting || !isFormValid}
              className="w-full rounded-md"
            >
              {isSubmitting ? 'Creating...' : 'Create'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  )
}
